/**
 * Timeline 组件 — 时间轴首页
 * 纵向全屏滚动，每屏一把伞的概览
 */

import { visibleUmbrellas } from '../app/data.js';
import { navigateToDetail } from '../app/router.js';
import { createIcon } from '../app/utils.js';

let currentIndex = 0;
let isScrolling = false;
let directTimer = null;

/** 已启用史实交互动效的时期 */
const INTERACTIVE_IDS = ['pre_qin', 'han', 'wei_jin', 'song', 'yuan', 'ming', 'qing', 'xihu_silk', 'guohuo'];

/** 各时期动效依据提示（文字落位后原地渐入） */
const ANIM_NOTES = {
  pre_qin: '《孔子家语》「命从者皆持盖」，华盖为树立之仪仗',
  han: '秦陵铜车马伞盖为插装式盖座，车盖可装卸',
  song: '宋人「张帛避雨谓之伞」，纸伞已可开合',
  yuan: '伞自宋代起可开合，油布伞以棉布涂桐油为面、较油纸伞更坚固耐用',
  qing: '伞自宋代起可开合，清代油纸伞工艺鼎盛',
  xihu_silk: '伞自宋代起可开合，西湖绸伞为国家级非遗',
  guohuo: '伞自宋代起可开合，八角伞为民国实物'
};

/** 各时期交互提示语 */
const ANIM_HINTS = {
  pre_qin: '轻触立盖',
  han: '轻触装盖',
  wei_jin: '轻触启幔',
  song: '轻触开伞',
  yuan: '轻触开伞',
  ming: '轻触开伞',
  qing: '轻触开伞',
  xihu_silk: '轻触开伞',
  guohuo: '轻触开伞'
};

/**
 * 渲染时间轴首页
 */
export function renderTimeline() {
  const container = document.getElementById('page-home');
  if (!container) return;

  container.innerHTML = `
    <div class="timeline-page" id="timeline-scroll">
      ${visibleUmbrellas.map((item, index) => renderTimelineItem(item, index)).join('')}
    </div>
    <div class="timeline-indicator" id="timeline-indicator">
      ${visibleUmbrellas.map((_, i) => `<div class="dot ${i === 0 ? 'active' : ''}" data-index="${i}"></div>`).join('')}
    </div>
    <div class="timeline-progress" id="timeline-progress"></div>
  `;

  bindEvents();
}

/**
 * 渲染单个时间轴条目
 */
function renderTimelineItem(item, index) {
  const hasBg = item.background && item.background.trim() !== '';
  const isInteractive = INTERACTIVE_IDS.includes(item.id);

  return `
    <div class="timeline-item ${index === 0 ? 'tl-active ' : ''}${isInteractive ? 'tl-interactive' : ''} ${hasBg ? 'tl-has-bg' : ''}" data-index="${index}" data-id="${item.id}">
      ${hasBg ? `
        <img class="bg-image" src="${item.background}" alt="${item.name}背景" loading="eager" fetchpriority="high">
        <div class="bg-overlay"></div>
      ` : '<div class="bg-overlay"></div>'}

      <div class="timeline-content">
        ${isInteractive ? renderInteractiveContent(item, index) : renderClassicContent(item, index)}
      </div>
  </div>
  `;
}

/**
 * 经典卡片内容（其他时期保持原样，不改动）
 */
function renderClassicContent(item, index) {
  return `
    <div class="timeline-umbrella-silhouette">
      <svg viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
        <path d="M50 10 C50 10 10 35 5 55 C5 55 20 50 50 50 C80 50 95 55 95 55 C90 35 50 10 50 10Z" />
        <line x1="50" y1="50" x2="50" y2="115" />
        <path d="M50 115 C50 115 45 118 42 120" />
      </svg>
    </div>

    <div class="timeline-era-label">
      <span class="line"></span>
      <span class="text">${item.era}</span>
      <span class="line"></span>
    </div>

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <p class="timeline-desc">${item.description}</p>

    <div class="timeline-cta">
      <button class="btn btn-primary" data-action="view" data-id="${item.id}">
        观展
      </button>
      ${index < visibleUmbrellas.length - 1 ? `
        <span class="timeline-scroll-hint">
          向下滑动
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 9l6 6 6-6"/></svg>
        </span>
      ` : ''}
    </div>
  `;
}

/**
 * 交互卡片公共块：时期（放大）+ 纪年
 */
function eraBlock(item) {
  return `
    <div class="timeline-era-label">
      <span class="line"></span>
      <span class="text">${item.era}</span>
      <span class="line"></span>
    </div>
    <p class="timeline-era-years">${item.period}</p>
  `;
}

/**
 * 交互卡片公共块：动效依据提示 + 观展按钮 + 出口
 */
function ctaBlock(item, index) {
  const note = ANIM_NOTES[item.id] || '';
  return `
    <div class="timeline-cta">
      ${note ? `<p class="anim-note">${note}</p>` : ''}
      <button class="btn btn-primary" data-action="view" data-id="${item.id}">
        观展
      </button>
      ${index < visibleUmbrellas.length - 1 ? `
        <span class="timeline-scroll-hint">
          向下滑动
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 9l6 6 6-6"/></svg>
        </span>
      ` : ''}
    </div>

    <span class="umbreline-direct" data-action="view" data-id="${item.id}">直接观展 ›</span>
  `;
}

/**
 * 交互卡片内容分发
 */
function renderInteractiveContent(item, index) {
  if (item.id === 'pre_qin') return renderPreqinDemo(item, index);
  if (item.id === 'han') return renderHanDemo(item, index);
  if (item.id === 'wei_jin') return renderWeijinDemo(item, index);
  if (item.id === 'song') return renderSongDemo(item, index);
  if (item.id === 'yuan') return renderYuanDemo(item, index);
  if (item.id === 'ming') return renderMingDemo(item, index);
  if (item.id === 'qing') return renderQingDemo(item, index);
  if (item.id === 'xihu_silk') return renderXihuDemo(item, index);
  return renderGuohuoDemo(item, index);
}

/**
 * 先秦·华盖 — 降临就位（华盖不可收合，为树立之仪仗）
 * 展示顺序：时期 → 伞名 → 线稿图 → 介绍文字
 */
function renderPreqinDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.pre_qin}">
      <svg class="u-static" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="100" y1="50" x2="100" y2="202" />
        <line x1="82" y1="202" x2="118" y2="202" />
      </svg>
      <svg class="u-canopy" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="46" r="3" />
        <path d="M28 78 Q100 52 172 78" />
        <path d="M28 78 Q100 62 172 78" />
        <g class="u-tassels">
          <line x1="28" y1="78" x2="28" y2="92" /><circle cx="28" cy="94" r="1.6" />
          <line x1="64" y1="72" x2="64" y2="86" /><circle cx="64" cy="88" r="1.6" />
          <line x1="100" y1="70" x2="100" y2="84" /><circle cx="100" cy="86" r="1.6" />
          <line x1="136" y1="72" x2="136" y2="86" /><circle cx="136" cy="88" r="1.6" />
          <line x1="172" y1="78" x2="172" y2="92" /><circle cx="172" cy="94" r="1.6" />
        </g>
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.pre_qin}</span>
    </div>

    <p class="timeline-desc fall-block">${splitHalfChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 汉代·车盖 — 沿杆滑下、插装锁定（秦陵铜车马插装式盖座）
 */
function renderHanDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.han}">
      <svg class="u-static" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <line x1="100" y1="70" x2="100" y2="170" />
        <circle cx="100" cy="186" r="16" />
        <line x1="100" y1="170" x2="100" y2="202" />
        <line x1="86" y1="178" x2="114" y2="194" />
        <line x1="114" y1="178" x2="86" y2="194" />
      </svg>
      <svg class="u-canopy" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="50" r="3" />
        <path d="M24 70 Q100 44 176 70" />
        <path d="M24 70 Q100 88 176 70" />
        <line x1="100" y1="58" x2="24" y2="70" />
        <line x1="100" y1="58" x2="62" y2="77" />
        <line x1="100" y1="58" x2="100" y2="80" />
        <line x1="100" y1="58" x2="138" y2="77" />
        <line x1="100" y1="58" x2="176" y2="70" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.han}</span>
    </div>

    <p class="timeline-desc fall-block">${splitHalfChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 宋代·绿油纸伞 — 收伞态 → 点击开伞 → 文字散落
 */
function renderSongDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.song}">
      <svg class="u-closed" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="8" r="2.5" />
        <circle cx="100" cy="14.5" r="3.5" />
        <line x1="100" y1="18" x2="100" y2="24" />
        <path d="M100 24 C107 52 109 92 105 140 C104 154 96 154 95 140 C91 92 93 52 100 24 Z" />
        <path d="M100 34 C103 70 103 105 100 138" />
        <path d="M96 40 C94 75 95 108 97 140" />
        <line x1="100" y1="150" x2="100" y2="196" />
      </svg>
      <svg class="u-open" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="7" r="3" />
        <circle cx="100" cy="14.5" r="4.5" />
        <path d="M100 20 C64 28 30 50 18 84 Q30 77 42 81 Q54 73 66 77 Q78 69 90 73 Q95 70 100 71 Q105 70 110 73 Q122 69 134 77 Q146 73 158 81 Q170 77 182 84 C170 50 136 28 100 20 Z" />
        <line x1="100" y1="22" x2="18" y2="84" />
        <line x1="100" y1="22" x2="42" y2="81" />
        <line x1="100" y1="22" x2="66" y2="77" />
        <line x1="100" y1="22" x2="90" y2="73" />
        <line x1="100" y1="22" x2="110" y2="73" />
        <line x1="100" y1="22" x2="134" y2="77" />
        <line x1="100" y1="22" x2="158" y2="81" />
        <line x1="100" y1="22" x2="182" y2="84" />
        <line x1="100" y1="71" x2="100" y2="196" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.song}</span>
    </div>

    <p class="timeline-desc fall-block">${splitChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 标点避头尾：开括号与后字同组，闭合标点与前字同组（逐字拆分后浏览器不再自动避标点）
 */
function groupUnits(text) {
  const units = [];
  let pending = '';
  for (const ch of Array.from(text)) {
    if ('「『《（〈'.includes(ch)) {
      pending += ch;
      continue;
    }
    const unit = pending + ch;
    pending = '';
    if ('」』）》？，。；：、！…—'.includes(ch) && units.length) {
      units[units.length - 1] += unit;
    } else {
      units.push(unit);
    }
  }
  if (pending) units.push(pending);
  return units;
}

/**
 * 介绍文字逐字拆散（雨落效果：各字带确定性的随机水平偏移、旋转与延迟）
 */
function splitChars(text) {
  return groupUnits(text).map((unit, i) => {
    const dx = ((i * 37) % 61) - 30;                       // -30 ~ 30px 水平散落
    const rot = ((i * 29) % 41) - 20;                      // -20 ~ 20 度倾斜
    const d = (0.7 + ((i * 53) % 50) / 100).toFixed(2);    // 0.7 ~ 1.19s 错落延迟
    return `<span class="fall-char" style="--dx:${dx}px;--rot:${rot}deg;--d:${d}s">${unit}</span>`;
  }).join('');
}

/**
 * 介绍文字逐字拆成上下两半（下半先就位，上半随后落下回正，与伞体动画同步触发）
 */
function splitHalfChars(text) {
  return groupUnits(text).map((unit, i) => {
    const d = (i * 0.018).toFixed(3);            // 逐字错峰 18ms
    const r = i % 2 ? 2 : -2;                    // 交替微旋，落下回正
    return `<span class="half-char" style="--d:${d}s;--r:${r}deg"><span class="half half-b">${unit}</span><span class="half half-t" aria-hidden="true">${unit}</span></span>`;
  }).join('');
}

/**
 * 介绍文字逐字柔和渐显（魏晋垂幔轻启：文字如幔后渐出，与启幔同步触发）
 */
function splitVeilChars(text) {
  return groupUnits(text).map((unit, i) => {
    const d = (0.25 + i * 0.022).toFixed(3);     // 幔启后逐字缓出
    return `<span class="veil-char" style="--d:${d}s">${unit}</span>`;
  }).join('');
}

/**
 * 魏晋·青罗伞盖 — 垂幔轻启（仪仗用盖，是否可开合史料未载，故不作开合）
 */
function renderWeijinDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.wei_jin}">
      <svg class="u-static" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="48" r="3" />
        <path d="M30 80 Q100 54 170 80" />
        <path d="M30 80 Q100 64 170 80" />
        <line x1="100" y1="80" x2="100" y2="200" />
        <line x1="82" y1="200" x2="118" y2="200" />
      </svg>
      <svg class="u-curtain" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <g class="u-curtain-l">
          <path d="M32 80 Q66 88 100 89 L100 116 Q64 114 34 106 Z" />
          <path d="M54 83 Q56 96 54 109" />
          <path d="M78 86 Q79 99 78 112" />
        </g>
        <g class="u-curtain-r">
          <path d="M168 80 Q134 88 100 89 L100 116 Q136 114 166 106 Z" />
          <path d="M146 83 Q144 96 146 109" />
          <path d="M122 86 Q121 99 122 112" />
        </g>
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.wei_jin}</span>
    </div>

    <p class="timeline-desc fall-block">${splitVeilChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 明代·货郎伞 — 方形伞盖、撑于货架立柱（国博《货郎图》为原型），可开合
 */
function renderMingDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.ming}">
      <svg class="u-closed" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="14" r="2.5" />
        <line x1="100" y1="18" x2="100" y2="30" />
        <path d="M94 30 L106 30 L109 146 L91 146 Z" />
        <line x1="100" y1="34" x2="100" y2="142" />
        <line x1="100" y1="146" x2="100" y2="198" />
        <line x1="100" y1="198" x2="88" y2="208" />
        <line x1="100" y1="198" x2="112" y2="208" />
      </svg>
      <svg class="u-open" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="12" r="3" />
        <line x1="100" y1="16" x2="100" y2="56" />
        <path d="M40 62 L160 62 L168 80 L160 88 L40 88 L32 80 Z" />
        <line x1="60" y1="62" x2="59" y2="88" />
        <line x1="80" y1="62" x2="79" y2="88" />
        <line x1="100" y1="62" x2="100" y2="88" />
        <line x1="120" y1="62" x2="121" y2="88" />
        <line x1="140" y1="62" x2="141" y2="88" />
        <line x1="100" y1="88" x2="100" y2="198" />
        <line x1="100" y1="198" x2="88" y2="208" />
        <line x1="100" y1="198" x2="112" y2="208" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.ming}</span>
    </div>

    <p class="timeline-desc fall-block">${splitChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 清代·余杭油纸伞 — 深穹圆盖、直柄葫芦顶，可开合
 */
function renderQingDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.qing}">
      <svg class="u-closed" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="9" r="2.5" />
        <circle cx="100" cy="15.5" r="3.5" />
        <line x1="100" y1="19" x2="100" y2="26" />
        <path d="M100 26 C109 54 111 94 106 142 C105 156 95 156 94 142 C89 94 91 54 100 26 Z" />
        <path d="M100 36 C103 72 103 106 100 140" />
        <path d="M96 42 C94 76 95 108 97 142" />
        <line x1="100" y1="152" x2="100" y2="196" />
      </svg>
      <svg class="u-open" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="8" r="3" />
        <circle cx="100" cy="15.5" r="4.5" />
        <path d="M100 22 C60 32 32 60 26 94 Q38 87 50 91 Q62 83 74 87 Q86 79 98 83 Q102 81 106 83 Q118 79 130 87 Q142 83 154 91 Q166 87 174 94 C168 60 140 32 100 22 Z" />
        <line x1="100" y1="24" x2="26" y2="94" />
        <line x1="100" y1="24" x2="50" y2="91" />
        <line x1="100" y1="24" x2="74" y2="87" />
        <line x1="100" y1="24" x2="98" y2="83" />
        <line x1="100" y1="24" x2="106" y2="83" />
        <line x1="100" y1="24" x2="130" y2="87" />
        <line x1="100" y1="24" x2="154" y2="91" />
        <line x1="100" y1="24" x2="174" y2="94" />
        <line x1="100" y1="84" x2="100" y2="196" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.qing}</span>
    </div>

    <p class="timeline-desc fall-block">${splitChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 民国·西湖绸伞 — 浅盖长柄、淡竹为骨丝绸为面，可开合
 */
function renderXihuDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.xihu_silk}">
      <svg class="u-closed" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="10" r="2.5" />
        <circle cx="100" cy="16" r="3" />
        <line x1="100" y1="19" x2="100" y2="26" />
        <path d="M100 26 C105 56 107 96 103 144 C102 156 98 156 97 144 C93 96 95 56 100 26 Z" />
        <path d="M100 36 C102 72 102 106 100 140" />
        <line x1="100" y1="150" x2="100" y2="198" />
      </svg>
      <svg class="u-open" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="9" r="3" />
        <circle cx="100" cy="16" r="4" />
        <path d="M100 22 C68 30 40 48 32 76 Q44 70 56 73 Q68 67 80 70 Q92 64 100 66 Q108 64 120 70 Q132 67 144 73 Q156 70 168 76 C160 48 132 30 100 22 Z" />
        <line x1="100" y1="24" x2="32" y2="76" />
        <line x1="100" y1="24" x2="56" y2="73" />
        <line x1="100" y1="24" x2="80" y2="70" />
        <line x1="100" y1="24" x2="100" y2="66" />
        <line x1="100" y1="24" x2="120" y2="70" />
        <line x1="100" y1="24" x2="144" y2="73" />
        <line x1="100" y1="24" x2="168" y2="76" />
        <line x1="100" y1="66" x2="100" y2="198" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.xihu_silk}</span>
    </div>

    <p class="timeline-desc fall-block">${splitChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 元代·油布伞 — 宽浅布面圆盖、伞缘骨梢外露、竹节直柄，可开合
 */
function renderYuanDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.yuan}">
      <svg class="u-closed" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M95 5 L105 5 L106 14 L94 14 Z" />
        <line x1="100" y1="14" x2="100" y2="26" />
        <path d="M100 26 C110 54 113 94 108 142 C107 156 93 156 92 142 C87 94 90 54 100 26 Z" />
        <path d="M100 36 C104 72 104 106 101 140" />
        <path d="M96 42 C93 76 94 108 97 142" />
        <line x1="100" y1="152" x2="100" y2="196" />
        <line x1="96" y1="170" x2="104" y2="170" />
      </svg>
      <svg class="u-open" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M94 5 L106 5 L107 14 L93 14 Z" />
        <line x1="100" y1="14" x2="100" y2="24" />
        <path d="M100 24 C58 34 30 64 24 100 Q37 93 50 97 Q63 89 76 93 Q88 85 100 89 Q112 85 124 93 Q137 89 150 97 Q163 93 176 100 C170 64 142 34 100 24 Z" />
        <line x1="100" y1="26" x2="24" y2="100" />
        <line x1="100" y1="26" x2="50" y2="97" />
        <line x1="100" y1="26" x2="76" y2="93" />
        <line x1="100" y1="26" x2="100" y2="89" />
        <line x1="100" y1="26" x2="124" y2="93" />
        <line x1="100" y1="26" x2="150" y2="97" />
        <line x1="100" y1="26" x2="176" y2="100" />
        <line x1="24" y1="100" x2="21" y2="105" />
        <line x1="76" y1="93" x2="74" y2="98" />
        <line x1="124" y1="93" x2="126" y2="98" />
        <line x1="176" y1="100" x2="179" y2="105" />
        <line x1="100" y1="90" x2="100" y2="196" />
        <line x1="96" y1="150" x2="104" y2="150" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.yuan}</span>
    </div>

    <p class="timeline-desc fall-block">${splitChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 民国·「请用国货」八角油纸伞 — 八边形伞面轮廓（32 骨实物为据），可开合
 */
function renderGuohuoDemo(item, index) {
  return `
    ${eraBlock(item)}

    <h2 class="timeline-name">${item.name}</h2>
    <p class="timeline-name-en">${item.nameEn}</p>

    <div class="umbreline" id="umbreline-${item.id}" role="button" tabindex="0" aria-label="${ANIM_HINTS.guohuo}">
      <svg class="u-closed" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="10" r="3" />
        <line x1="100" y1="14" x2="100" y2="24" />
        <path d="M100 24 L109 82 L104 142 L96 142 L91 82 Z" />
        <path d="M100 34 L103 84 L101 140" />
        <path d="M100 34 L97 84 L99 140" />
        <line x1="100" y1="148" x2="100" y2="196" />
      </svg>
      <svg class="u-open" viewBox="0 0 200 220" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="100" cy="10" r="3" />
        <line x1="100" y1="14" x2="100" y2="26" />
        <path d="M100 26 L62 44 L32 88 L56 75 L88 65 L112 65 L144 75 L168 88 L138 44 Z" />
        <line x1="100" y1="28" x2="32" y2="88" />
        <line x1="100" y1="28" x2="56" y2="75" />
        <line x1="100" y1="28" x2="88" y2="65" />
        <line x1="100" y1="28" x2="112" y2="65" />
        <line x1="100" y1="28" x2="144" y2="75" />
        <line x1="100" y1="28" x2="168" y2="88" />
        <line x1="100" y1="65" x2="100" y2="196" />
      </svg>
      <span class="umbreline-hint">${ANIM_HINTS.guohuo}</span>
    </div>

    <p class="timeline-desc fall-block">${splitChars(item.description)}</p>

    ${ctaBlock(item, index)}
  `;
}

/**
 * 触发交互卡动效（开伞/立盖/装盖，均由 CSS 类驱动）
 */
function openUmbrella(umbreline) {
  const item = umbreline.closest('.timeline-item');
  if (!item || item.classList.contains('umbrella-open')) return;
  item.classList.add('umbrella-open');
}

/**
 * 绑定事件
 */
function bindEvents() {
  const scrollContainer = document.getElementById('timeline-scroll');
  const indicator = document.getElementById('timeline-indicator');
  const progress = document.getElementById('timeline-progress');

  if (!scrollContainer) return;

  // 滚动监听 — 更新指示器
  scrollContainer.addEventListener('scroll', () => {
    const scrollTop = scrollContainer.scrollTop;
    const itemHeight = scrollContainer.clientHeight;
    const index = Math.round(scrollTop / itemHeight);

    if (index !== currentIndex && index >= 0 && index < visibleUmbrellas.length) {
      currentIndex = index;
      updateIndicator(index);
    }

    // 进度条
    if (progress) {
      const maxScroll = scrollContainer.scrollHeight - itemHeight;
      const percent = (scrollTop / maxScroll) * 100;
      progress.style.height = `${percent}%`;
    }
  });

  // 点击观展按钮
  scrollContainer.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action="view"]');
    if (btn) {
      const id = btn.dataset.id;
      navigateToDetail(id);
    }
  });

  // 点击指示器跳转
  if (indicator) {
    indicator.addEventListener('click', (e) => {
      const dot = e.target.closest('.dot');
      if (dot) {
        const idx = parseInt(dot.dataset.index, 10);
        scrollToItem(idx);
      }
    });
  }

  // 交互卡动效（开伞/立盖/装盖）
  scrollContainer.addEventListener('click', (e) => {
    const um = e.target.closest('.umbreline');
    if (um) openUmbrella(um);
  });
  scrollContainer.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const um = e.target.closest('.umbreline');
    if (um) {
      e.preventDefault();
      openUmbrella(um);
    }
  });

  // 3 秒未触发则给出「直接观展」出口
  if (directTimer) clearTimeout(directTimer);
  directTimer = setTimeout(() => {
    document
      .querySelectorAll('.timeline-item.tl-interactive:not(.umbrella-open)')
      .forEach(el => el.classList.add('show-direct'));
  }, 3000);

  // 减弱动态效果：直接呈现终态
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document
      .querySelectorAll('.timeline-item.tl-interactive')
      .forEach(el => el.classList.add('umbrella-open'));
  }
}

/**
 * 更新指示器状态
 */
function updateIndicator(index) {
  const dots = document.querySelectorAll('.timeline-indicator .dot');
  dots.forEach((dot, i) => {
    dot.classList.toggle('active', i === index);
  });
  // 标记当前屏卡片：仅它播放动效（低端/沙箱 webview 减负，防合成层爆显存黑屏）
  document.querySelectorAll('.timeline-item').forEach((el, i) => {
    el.classList.toggle('tl-active', i === index);
  });
}

/**
 * 滚动到指定条目
 */
function scrollToItem(index) {
  const scrollContainer = document.getElementById('timeline-scroll');
  if (!scrollContainer) return;

  const itemHeight = scrollContainer.clientHeight;
  scrollContainer.scrollTo({
    top: itemHeight * index,
    behavior: 'smooth'
  });
}
